import { ListSourcedValue, Option, OptionGroup, Sourceable, SourcedValue } from '../option.js';
import { pathToRoot, PropertyPathWalker, PropertyValue, SourceType } from './PropertyPathWalker.js';
import { Schema } from '../schema.js';
import { PModel, PropertyPath } from '../engine.js';
import { TypeKind } from '../types.js';
import { ModelSystemProperties } from './ModelSystemProperties.js';
import { optionIdSymbol } from '../engine-impl.js';

export class SourceUtils {
  constructor(private schema: Schema) {
  }

  isSourceableProperty(model: PModel, path: PropertyPath): boolean {
    return this.get(this.getPropertyValue(model, path)).isSourceable;
  }

  isPropertyHasSources(model: PModel, path: PropertyPath): boolean {
    return Array.isArray(this.get(this.getPropertyValue(model, path)).sources);
  }

  isSourceProvider(propertyValue: PropertyValue): boolean {
    const needSource = propertyValue.property.opts.needSource;
    return needSource === true || Array.isArray(needSource) || needSource === 'specific';
  }

  isSourceProviderHasSources(model: any, name: string): boolean {
    const sourceFromModel = ModelSystemProperties.getSourceFromModel(model, name);
    return sourceFromModel !== undefined && sourceFromModel !== null;
  }

  sourceProviderType(propertyValue: PropertyValue): SourceType {
    const needSource = propertyValue.property.opts.needSource;
    if (needSource === true && this.schema.getType(propertyValue.property.type).kind === TypeKind.Object) {
      return SourceType.Single;
    } else if (needSource === true && this.schema.getType(propertyValue.property.type).kind === TypeKind.List) {
      return SourceType.ListCommon;
    } else if (Array.isArray(needSource) && this.schema.getType(propertyValue.property.type).kind === TypeKind.List) {
      return SourceType.ListCommonForNested;
    } else if (needSource === 'specific') {
      return SourceType.ListSpecific;
    }
    throw new Error(`'Can't determine type of source provider for ${pathToRoot(propertyValue)}`)
  }

  // sourceType(path: PropertyPath): SourceType {
  //   const propertyWalker = new PropertyPathWalker(path, this.schema, this.model, this.typeRef);
  //   return this.get(propertyWalker.getPropertyValueByName()).type;
  // }

  sourcesType(model: PModel, path: PropertyPath): SourceType {
    return this.get(this.getPropertyValue(model, path)).type;
  }

  getPropertySources(model: PModel, path: PropertyPath): Array<Option | OptionGroup> {
    return this.get(this.getPropertyValue(model, path)).sources;
  }

  setSourcesToModel(model: PModel, path: PropertyPath, sources: Sourceable): void {
    ModelSystemProperties.addSourceToModel(model, path, sources);
  }


  public static getInnerValue(value: SourcedValue | ListSourcedValue | Option | object) {
    if (value instanceof SourcedValue) {
      if (Array.isArray(value.value)) {
        return value.value.map(v => SourceUtils.getModelValue(v));
      } else if (value.value instanceof Option) {
        return SourceUtils.getModelValue(value.value);
      } else if (value.value == null) {
        // support null value in SourcedValue
        return null;
      } else {
        throw new Error('Unsupported structure')
      }
    } else if (value instanceof ListSourcedValue) {
      return value.value;
    } else if (value instanceof Option) {
      return SourceUtils.getModelValue(value);
    } else {
      return value;
    }
  }

  private get(propertyValue: PropertyValue): { isSourceable: boolean, sources: Array<Option | OptionGroup>, type: SourceType } {
    return this._get(propertyValue, propertyValue.property.name);
  }

  private _get(propertyValue: PropertyValue, lookingPropertyName: PropertyPath): { isSourceable: boolean, sources: Array<Option | OptionGroup>, type: SourceType } {
    if (propertyValue.property.opts.needSource === true) {
      // single  or listCommon
      let source = propertyValue.parent ? ModelSystemProperties.getSource(propertyValue) : undefined;
      return {
        type: this.schema.getType(propertyValue.property.type).kind === TypeKind.List ? SourceType.ListCommon : SourceType.Single,
        isSourceable: true,
        sources: source ? source.getSource() : undefined
      };
    } else if (propertyValue.parent) {
      let parent = propertyValue.parent;

      if (parent.property.opts.needSource === true && this.schema.getType(parent.property.type).kind === TypeKind.List) {
        // element in listCommon
        let source = parent.parent ? ModelSystemProperties.getSource(parent) : undefined;
        return {
          type: SourceType.ListCommonElement,
          isSourceable: true,
          sources: source ? source.getSource() : undefined
        };
      } else if (parent.property.opts.needSource === 'specific') {
        // listSpecific
        let source = parent.parent ? ModelSystemProperties.getSource(parent)[propertyValue.property.name] : undefined;
        return {
          type: SourceType.ListSpecific,
          isSourceable: true,
          sources: source ? source.getSource(propertyValue.property.name) : undefined
        };
      } else if (Array.isArray(parent.property.opts.needSource) && this.schema.getType(parent.property.type).kind === TypeKind.List &&
        (parent.property.opts.needSource as string[]).includes(lookingPropertyName)
      ) {
        // listCommonForNested e.g. - 'list.item'
        // TODO: Check if getSource support PropertyPath
        let source = parent.parent ? ModelSystemProperties.getSource(parent) : undefined;
        return {
          type: SourceType.ListCommonForNested,
          isSourceable: true,
          sources: source ? source.getSource(lookingPropertyName) : undefined
        };
      } else if (parent.parent) {
        parent = parent.parent;
        if (Array.isArray(parent.property.opts.needSource) && this.schema.getType(parent.property.type).kind === TypeKind.List &&
          (parent.property.opts.needSource as string[]).includes(lookingPropertyName)
        ) {
          // listCommonForNested e.g. - 'list.0.item'
          let source = parent.parent ? ModelSystemProperties.getSource(parent) : undefined;
          return {
            type: SourceType.ListCommonForNestedElement,
            isSourceable: true,
            sources: source ? source.getSource(lookingPropertyName) : undefined
          };
        }
      }
    }
    if (propertyValue.parent) {
      return this._get((propertyValue.parent), propertyValue.parent.property.name + '.' + lookingPropertyName);
    }
    return {
      type: undefined,
      isSourceable: false,
      sources: undefined
    };
  }

  private getPropertyValue(model: PModel, path: PropertyPath): PropertyValue {
    const walker = new PropertyPathWalker(path, this.schema, model, ModelSystemProperties.getTypeRef(model));
    return walker.getPropertyValue();
  }

  static getModelValue(v: Option) {
    return v.model;
    // return {...v.model, _id: v._id };
  }

  static setOptionId(target: object, id: string) {
    target['_id'] = id;
  }

  static setProductId(model: any, _id: any) {
    model[optionIdSymbol] = _id;
  }

  static setProductGroupId = SourceUtils.setProductId;

  private getListCommonForNestedPropertyPath(propertyValue: PropertyValue) {
    if (propertyValue) {
    }

    return { isSourceable: false, sources: undefined, type: undefined };
  }
}
